import { getGatewayBaseUrl, runtimeConfig } from "../config";
import type { EmployeeRuntimeActor } from "../gateway/identity-client";
import {
  readLocalEmployeeSession,
  saveCoreEmployeeSession,
} from "../local-store/employee-session";
import {
  readTerminalCredential,
  type TerminalCredentialState,
} from "../local-store/terminal-state";

type RecoveryPayload = EmployeeRuntimeActor & {
  recovered?: boolean;
  runtime_session_token?: string;
  request_key?: string;
  reason_code?: string;
};

type RecoveryEnvelope = {
  ok: boolean;
  data?: RecoveryPayload;
  error?: string;
};

type EmployeeSessionRecoveryReason =
  | "LOCAL_EMPLOYEE_SESSION_MISSING"
  | "LOCAL_EMPLOYEE_SESSION_INVALIDATED";

function requestKey() {
  const id =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `pos-frontline-session-recover:${id}`;
}

function sameTerminalScope(
  actor: EmployeeRuntimeActor,
  terminal: TerminalCredentialState,
) {
  const identity = terminal.identity;
  if (!identity) return false;

  return (
    actor.organization_id === identity.organization_id &&
    actor.node_id === identity.node_id &&
    actor.branch_id === identity.branch_id &&
    actor.device_credential_id === identity.credential_id &&
    actor.device_credential_version === identity.credential_version &&
    actor.runtime_session_status === "ACTIVE"
  );
}

function actorFromRecovery(payload: RecoveryPayload): EmployeeRuntimeActor {
  const {
    recovered: _recovered,
    runtime_session_token: _runtimeSessionToken,
    request_key: _requestKey,
    reason_code: _reasonCode,
    ...actor
  } = payload;
  return actor;
}

async function postRecovery(
  baseUrl: string,
  deviceSecret: string,
  recoveryReason: EmployeeSessionRecoveryReason,
): Promise<RecoveryEnvelope> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 12_000);

  try {
    const response = await fetch(`${baseUrl.replace(/\/+$/, "")}/api/pos/identity/session/recover`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        device_secret: deviceSecret,
        request_key: requestKey(),
        client_context: {
          runtime: "WEBPOS",
          origin: window.location.origin,
          build_mode: runtimeConfig.buildMode,
          recovery_reason: recoveryReason,
        },
      }),
      signal: controller.signal,
      credentials: "omit",
    });

    const payload = (await response.json()) as RecoveryEnvelope;
    if (!response.ok) {
      return { ok: false, error: payload.error ?? `GATEWAY_HTTP_${response.status}` };
    }
    return payload;
  } catch (error) {
    return {
      ok: false,
      error:
        error instanceof DOMException && error.name === "AbortError"
          ? "GATEWAY_TIMEOUT"
          : "GATEWAY_UNREACHABLE",
    };
  } finally {
    window.clearTimeout(timeout);
  }
}

/**
 * Owner-contract recovery for an already-existing ACTIVE Employee Runtime Session.
 *
 * This never creates a new session and never infers an employee from Shift. It is safe to
 * call only as an exceptional healing path after local employee-session state is missing or
 * has just been rejected by the canonical session resolver. Access Core remains the final
 * authority and will fail closed when the session, employee, branch or terminal is no longer
 * eligible.
 */
export async function recoverEmployeeSessionFromCore(
  terminal: TerminalCredentialState | null,
  recoveryReason: EmployeeSessionRecoveryReason,
) {
  if (!terminal?.identity || !navigator.onLine) return false;

  const baseUrl = getGatewayBaseUrl();
  if (!baseUrl) return false;

  const deviceSecret = terminal.deviceSecret;
  const result = await postRecovery(baseUrl, deviceSecret, recoveryReason);
  if (!result.ok || !result.data?.recovered || !result.data.runtime_session_token) {
    return false;
  }

  // An enrollment/rotation in another tab can supersede this in-memory secret while the
  // recovery request is in flight. Never persist a session against that stale credential.
  const currentTerminal = await readTerminalCredential();
  if (
    !currentTerminal?.identity ||
    currentTerminal.deviceSecret !== deviceSecret ||
    currentTerminal.identity.credential_id !== terminal.identity.credential_id ||
    currentTerminal.identity.credential_version !== terminal.identity.credential_version
  ) {
    return false;
  }

  const actor = actorFromRecovery(result.data);
  if (!sameTerminalScope(actor, currentTerminal)) return false;

  await saveCoreEmployeeSession(result.data.runtime_session_token, actor);

  // Local read-after-write is the boot handoff boundary. Do not claim recovery until the
  // exact Core session is durably readable by the normal App boot path.
  const readback = await readLocalEmployeeSession();
  if (
    !readback ||
    readback.sessionToken !== result.data.runtime_session_token ||
    readback.actor.runtime_session_id !== actor.runtime_session_id ||
    !sameTerminalScope(readback.actor, currentTerminal)
  ) {
    return false;
  }

  return true;
}

/**
 * Exceptional pre-boot healing path only.
 *
 * Normal restart remains Local First: when the scoped employee session is already durable,
 * this function performs no network call. If local state is missing while Trusted Terminal
 * survived, ask Access Core to return only the already-active eligible session.
 */
export async function recoverMissingEmployeeSessionBeforeAppBoot(
  terminal: TerminalCredentialState | null,
) {
  if (!terminal?.identity || !navigator.onLine) return false;

  const local = await readLocalEmployeeSession();
  if (local && sameTerminalScope(local.actor, terminal)) return false;

  return recoverEmployeeSessionFromCore(terminal, "LOCAL_EMPLOYEE_SESSION_MISSING");
}
